import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Eye, FileText, Pencil, Plus, Trash2, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { LANGUAGE_OPTIONS } from "@/lib/locale";
import { TranslationDraftButton } from "@/components/admin/TranslationDraftButton";
import { requestTranslationDraft } from "@/lib/adminTranslation";
import { AdminSectionHeader } from "@/components/admin/AdminSectionHeader";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const API_ORIGIN = process.env.REACT_APP_BACKEND_URL || "";
const emptyDocument = {
  type: "certificate",
  title: "",
  description: "",
  pdf_url: "",
  language: "tr",
  order: 0,
  is_published: true,
  translations: {},
};

const resolveAsset = (url = "") => url.startsWith("/uploads/") ? `${API_ORIGIN}${url}` : url;

export const DocumentManager = ({ token }) => {
  const [documents, setDocuments] = useState([]);
  const [document, setDocument] = useState(emptyDocument);
  const [editingId, setEditingId] = useState(null);
  const [editLanguage, setEditLanguage] = useState("tr");
  const [pdfFile, setPdfFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadDocuments = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/documents`, { headers });
    setDocuments(data.documents || []);
  }, [headers]);

  useEffect(() => {
    loadDocuments().catch(() => toast.error("Belgeler yüklenemedi.")).finally(() => setIsLoading(false));
  }, [loadDocuments]);

  const editable = editLanguage === "tr"
    ? document
    : { ...document, ...(document.translations?.[editLanguage] || {}) };

  const changeLocalized = (key, value) => setDocument((current) => editLanguage === "tr"
    ? { ...current, [key]: value }
    : {
      ...current,
      translations: {
        ...(current.translations || {}),
        [editLanguage]: { ...(current.translations?.[editLanguage] || {}), [key]: value },
      },
    });

  const translateDocument = async () => {
    const result = await requestTranslationDraft({
      token,
      resourceType: "document",
      source: { title: document.title, description: document.description },
      translations: document.translations || {},
    });
    setDocument((current) => ({ ...current, translations: result.translations }));
    return result;
  };

  const resetForm = () => {
    setDocument(emptyDocument);
    setEditingId(null);
    setEditLanguage("tr");
    setPdfFile(null);
  };

  const uploadPdf = async () => {
    if (!pdfFile) return document.pdf_url;
    const body = new FormData();
    body.append("file", pdfFile);
    const { data } = await axios.post(`${API}/admin/document-uploads`, body, { headers });
    return data.file.url;
  };

  const save = async (event) => {
    event.preventDefault();
    if (!editingId && !pdfFile) {
      toast.error("Bir PDF dosyası seçin.");
      return;
    }
    setIsSaving(true);
    try {
      const pdf_url = await uploadPdf();
      const payload = { ...document, pdf_url };
      await axios({
        method: editingId ? "patch" : "post",
        url: `${API}/admin/documents${editingId ? `/${editingId}` : ""}`,
        data: payload,
        headers,
      });
      toast.success(editingId ? "Belge güncellendi." : "Belge eklendi.");
      resetForm();
      await loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Belge kaydedilemedi.");
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = (item) => {
    setDocument({ ...emptyDocument, ...item, translations: item.translations || {} });
    setEditingId(item.id);
    setEditLanguage("tr");
    setPdfFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (item) => {
    if (!window.confirm(`“${item.title}” belgesini silmek istiyor musunuz?`)) return;
    try {
      await axios.delete(`${API}/admin/documents/${item.id}`, { headers });
      if (editingId === item.id) resetForm();
      await loadDocuments();
      toast.success("Belge silindi.");
    } catch {
      toast.error("Belge silinemedi.");
    }
  };

  return <section>
    <AdminSectionHeader eyebrow="PDF kütüphanesi" title="Sertifikalar ve kataloglar" description="PDF belgelerini ekleyin, sıralayın ve tüm dillerdeki başlıklarını yönetin." actions={<>
        <a href="/sertifikalar" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-detail bg-surface px-4 py-2 text-sm font-semibold"><Eye className="h-4 w-4" /> Sertifikalar</a>
        <a href="/kataloglar" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-detail bg-surface px-4 py-2 text-sm font-semibold"><Eye className="h-4 w-4" /> Kataloglar</a>
      </>} />

    <form onSubmit={save} className="mt-7 grid gap-5 rounded-3xl border border-detail bg-surface p-5 sm:p-7 lg:grid-cols-2">
      <label className="text-sm font-semibold">Belge türü
        <select value={document.type} onChange={(event) => setDocument((current) => ({ ...current, type: event.target.value }))} className="field mt-2">
          <option value="certificate">Sertifika</option>
          <option value="catalog">Katalog</option>
        </select>
      </label>
      <label className="text-sm font-semibold">Belgenin dili
        <select value={document.language} onChange={(event) => setDocument((current) => ({ ...current, language: event.target.value }))} className="field mt-2">
          {LANGUAGE_OPTIONS.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
        </select>
      </label>
      <label className="text-sm font-semibold">Düzenlenen metin dili
        <select value={editLanguage} onChange={(event) => setEditLanguage(event.target.value)} className="field mt-2">
          {LANGUAGE_OPTIONS.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
        </select>
      </label>
      <label className="text-sm font-semibold">Sıra
        <input type="number" min="0" value={document.order} onChange={(event) => setDocument((current) => ({ ...current, order: Number(event.target.value) }))} className="field mt-2" />
      </label>
      <label className="text-sm font-semibold lg:col-span-2">Başlık
        <input required={editLanguage === "tr"} value={editable.title || ""} onChange={(event) => changeLocalized("title", event.target.value)} className="field mt-2" />
      </label>
      <label className="text-sm font-semibold lg:col-span-2">Açıklama
        <textarea value={editable.description || ""} onChange={(event) => changeLocalized("description", event.target.value)} className="field mt-2 min-h-24" />
      </label>
      <TranslationDraftButton
        disabled={!document.title.trim()}
        onTranslate={translateDocument}
        className="lg:col-span-2"
      />
      <label className="group lg:col-span-2 flex cursor-pointer items-center gap-4 rounded-2xl border border-dashed border-brand/35 bg-cream p-5 transition hover:border-brand">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand/10 text-brand"><UploadCloud className="h-5 w-5" /></span>
        <span className="min-w-0"><b className="block text-sm">{pdfFile?.name || (document.pdf_url ? "PDF yüklendi — değiştirmek için seçin" : "PDF dosyasını seçin")}</b><small className="text-ink/50">Yalnızca PDF · En fazla 25 MB</small></span>
        <input type="file" accept="application/pdf,.pdf" onChange={(event) => setPdfFile(event.target.files?.[0] || null)} className="sr-only" />
      </label>
      <label className="flex items-center gap-3 text-sm font-semibold">
        <input type="checkbox" checked={document.is_published} onChange={(event) => setDocument((current) => ({ ...current, is_published: event.target.checked }))} className="h-4 w-4 accent-brand" />
        Sitede yayınla
      </label>
      <div className="flex justify-end gap-2">
        {editingId && <button type="button" onClick={resetForm} className="inline-flex items-center gap-2 rounded-xl border border-detail px-5 py-3 font-semibold"><X className="h-4 w-4" /> Vazgeç</button>}
        <button disabled={isSaving} className="inline-flex items-center gap-2 rounded-xl bg-brand px-5 py-3 font-semibold text-cream disabled:opacity-50"><Plus className="h-4 w-4" />{isSaving ? "Kaydediliyor" : editingId ? "Belgeyi güncelle" : "Belgeyi ekle"}</button>
      </div>
    </form>

    <div className="mt-8 space-y-3">
      {isLoading && <p className="text-sm text-ink/50">Belgeler yükleniyor…</p>}
      {!isLoading && !documents.length && <div className="rounded-2xl border border-dashed border-detail p-8 text-center text-ink/50">Henüz PDF belgesi eklenmedi.</div>}
      {documents.map((item) => <article key={item.id} className="flex flex-col gap-4 rounded-2xl border border-detail bg-surface p-4 sm:flex-row sm:items-center">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-brand/10 text-brand"><FileText className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2"><b>{item.title}</b><span className="rounded-full bg-detail px-2 py-1 text-[10px] font-bold uppercase tracking-wider">{item.type === "certificate" ? "Sertifika" : "Katalog"}</span>{!item.is_published && <span className="rounded-full bg-red-50 px-2 py-1 text-[10px] font-bold text-red-600">Yayında değil</span>}</div>
          <small className="mt-1 block truncate text-ink/50">{item.language.toUpperCase()} · Sıra {item.order} · {item.description || "Açıklama yok"}</small>
        </div>
        <div className="flex gap-1">
          <a href={resolveAsset(item.pdf_url)} target="_blank" rel="noreferrer" className="p-2 text-ink/55" aria-label="PDF'i aç"><Eye className="h-4 w-4" /></a>
          <button type="button" onClick={() => startEdit(item)} className="p-2 text-brand" aria-label="Düzenle"><Pencil className="h-4 w-4" /></button>
          <button type="button" onClick={() => remove(item)} className="p-2 text-red-600" aria-label="Sil"><Trash2 className="h-4 w-4" /></button>
        </div>
      </article>)}
    </div>
  </section>;
};
